/**
 * Who may read this record, starting with the link that needs no account.
 *
 * The record link is the pane's headline capability: one token that hands
 * everything its owner chose to carry to whoever holds it, without an account
 * or membership in any organisation. It sits in one access list at the top of
 * the pane beside the organisation switches, since both answer the same
 * question: who may read this record.
 *
 * Every published item, listed further down by kind, carries its own audience
 * and its own link too. An item's own link opens only that item and never the
 * rest of the record, which is why its chip and the record's chip read
 * differently even though both open with "Anyone with". Whose-is-this is the
 * question that section exists to answer, and every row there says whose it
 * is.
 *
 * The pane withdraws a published item and never publishes one. Publishing
 * uploads the files, so it can only come from a machine that holds them, and
 * withdrawing must not need that machine to still exist. Anything the pane
 * cannot do therefore shows the command that can, said once for the section
 * with each row carrying its own argument, rather than a control that would
 * accept a click and then fail.
 *
 * Forgetting a machine is here for the mirror image of that reason. It erases,
 * so the endpoint takes a browser cookie, and a command line holds a machine
 * token that lives in configuration files. The pane is a person who signed in,
 * which makes it the only place the control can honestly live.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api } from '../api/client'
import { copyLabel, useCopyState } from '../components/copy'
import { InstallCommand } from '../install/InstallCommand'
import type {
  Machine, Me, Organisation, OrganisationMachine, OrganisationSharing, Setup, SetupItem, SetupState,
  SetupSummary,
} from '../api/types'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { describeAge } from '../status/status'
import { Aside, Label, Toggle } from './controls'

/** What identifies an item across two people's machines. */
const keyOf = (item: SetupItem) => `${item.kind}/${item.name}`

// An age, not a date. "reported 06/09/2026" makes a reader work out whether
// that is recent; "reported just now" is the fact they wanted. It is also what
// the status modal already does, from the same helper.
const reportedOn = (machine: Machine) => describeAge(machine.reported_at)

/**
 * Every item in a record, once.
 *
 * A person with two machines running the same plugin runs it once as far as a
 * comparison is concerned, so the first copy wins and the second is dropped.
 */
function itemsIn(record: Setup | null): Map<string, SetupItem> {
  const found = new Map<string, SetupItem>()
  for (const machine of record?.machines ?? []) {
    for (const item of machine.items) {
      if (!found.has(keyOf(item))) found.set(keyOf(item), item)
    }
  }
  return found
}

/** Every published item's reference, deduped across machines, in one order. */
function publishedReferences(record: Setup | null): string[] {
  return [...itemsIn(record).values()]
    .filter((entry) => entry.published)
    .map(keyOf)
    .sort()
}

/** Which of those the record link carries. */
function carriedReferences(record: Setup | null): string[] {
  return [...itemsIn(record).values()]
    .filter((entry) => entry.published && entry.on_setup_link)
    .map(keyOf)
    .sort()
}

/**
 * The two lines "Copy for an agent" puts on the clipboard.
 *
 * Two lines rather than the instructions themselves, because instructions
 * pasted into a message are frozen at the moment of copying and every copy
 * already sent stays wrong after the first edit. The second line is the whole
 * point: a URL is current every time it is read.
 */
function agentMessage(link: string): string {
  const guide = new URL('/agents/install-a-setup', link).toString()
  return `Set my setup up on this machine: ${link}\n`
    + `Read ${guide} first. It is written for you and says exactly what to do`
    + ' with that link.'
}

const line = { padding: '11px 0', borderTop: '1px solid var(--border)' } as const
// An origin is a repository path or a URL, and both are long enough to run off
// a phone. Wrapping is right at every width, so this is a property of the type
// rather than something a media query has to undo.
const mono = {
  font: '400 11.5px var(--font-machine)',
  color: 'var(--text-faint)',
  overflowWrap: 'anywhere',
} as const

function Command({ children }: { children: string }) {
  return (
    <code style={{ font: '500 11.5px var(--font-machine)', color: 'var(--text-dim)', background: 'var(--panel-strong)', borderRadius: radius.xs, padding: '3px 7px', display: 'inline-block', maxWidth: '100%', wordBreak: 'break-all' }}>
      {children}
    </code>
  )
}

/**
 * The kinds the pane lists, in order, and what each one is.
 *
 * `marketplace` is deliberately missing. A marketplace exists only to carry a
 * plugin, two of them alone in a rail read as an empty room, and a plugin
 * already shows its marketplace as its origin, which is where a reader was
 * looking for it anyway.
 */
const KINDS: ReadonlyArray<readonly [string, string, string]> = [
  ['plugin', 'Plugins', 'a name and a marketplace is the whole item'],
  ['mcp_server', 'MCP servers', 'an endpoint, never a credential'],
  ['skill', 'Skills', 'the pile where whose-is-it actually bites'],
  ['agent_definition', 'Subagents', 'one file each'],
  ['hook', 'Hooks', 'shell you wrote, and the value is the shell'],
  ['instructions', 'Instructions', 'your standing rules'],
]

interface Section { kind: string; label: string; rule: string; items: SetupItem[] }

/** Every kind with anything in it, in the pane's order and never empty. */
function sectionsOf(items: Iterable<SetupItem>): Section[] {
  const byKind = new Map<string, SetupItem[]>()
  for (const item of items) {
    if (item.kind === 'marketplace') continue
    byKind.set(item.kind, [...(byKind.get(item.kind) ?? []), item])
  }
  const listed = new Set(KINDS.map(([kind]) => kind))
  return [
    ...KINDS.map(([kind, label, rule]) => ({ kind, label, rule, items: byKind.get(kind) ?? [] })),
    // A kind reported before this list hears about it is shown under its own
    // name rather than quietly dropped, which is what folding taught us.
    ...[...byKind.keys()].filter((kind) => !listed.has(kind))
      .map((kind) => ({ kind, label: kind, rule: '', items: byKind.get(kind) as SetupItem[] })),
  ].filter((section) => section.items.length > 0)
}

/**
 * How the three states are told apart.
 *
 * Colour, not wording, because whose-is-this is the question the pane exists
 * to answer and it has to be answerable from across the room: the app's own
 * accent filled for what you wrote, outlined for what others did, and the
 * destructive red for the one state that cannot be published at all.
 */
const STATES: Record<SetupState, readonly [string, string, string, string]> = {
  yours: ['yours', 'var(--accent-bright)', 'var(--accent-wash)', 'var(--accent-edge)'],
  has_origin: ['origin', 'var(--accent)', 'transparent', 'var(--accent-edge)'],
  origin_unknown: ['origin missing', 'var(--deletions)', 'rgba(229,98,74,.12)', 'rgba(229,98,74,.3)'],
}

function StateChip({ item, owned }: { item: SetupItem; owned: boolean }) {
  const [label, colour, wash, edge] = STATES[item.state]
  return (
    <span
      data-state={item.state}
      style={{ font: '500 10px var(--font-machine)', letterSpacing: '.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', padding: '3px 7px', borderRadius: radius.xs, color: colour, background: wash, border: `1px solid ${edge}` }}
    >
      {item.state === 'yours' && !owned ? 'theirs' : label}
    </span>
  )
}

/**
 * Who reads one published item, and the one control that changes it.
 *
 * A chip that says the answer and opens a popover, rather than a row of
 * checkboxes on every line: a real record repeats these rows dozens of times
 * and the names are what a reader came for.
 *
 * An organisation whose record sharing is off is shown struck through even
 * when the item names it, because a scope that cannot take effect has to say so
 * where somebody is looking at it. The two switches compose one way only: the
 * record switch narrows and never widens.
 */
function Audience({ item, sharing, onChange, onLink, onRevoke, carried, onSetupLinkChange }: {
  item: SetupItem
  sharing: OrganisationSharing[]
  onChange: (organisations: string[]) => Promise<void>
  onLink: () => Promise<void>
  onRevoke: () => Promise<void>
  carried: boolean
  onSetupLinkChange: (next: boolean) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  // Which way it opens, decided at the click from where the chip actually is.
  // A record is dozens of rows long and this control lives on every published
  // one, so a popover that only ever opens downward puts Copy and Rotate below
  // the fold for the rows nearest the bottom.
  const [upward, setUpward] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [copyState, copy] = useCopyState()
  const named = new Set(item.audience)
  const effective = sharing.filter(
    (row) => row.enabled && named.has(row.organisation))

  // `Link` stays first because an item's own token is the narrower, more
  // deliberate act. The two are separate capabilities, and a chip that
  // conflated them would tell somebody they had revoked something they had
  // not.
  const label = item.shared_link ? 'Link'
    : carried ? 'Setup link'
      : effective.length === 0 ? 'You alone'
        : effective.length === 1
          ? (sharing.find((row) => row.organisation === effective[0].organisation)?.name
            ?? effective[0].organisation)
          : `${effective[0].name} +${effective.length - 1}`

  async function run(work: () => Promise<void>) {
    setBusy(true)
    setFailure(null)
    try {
      await work()
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'not saved')
    } finally {
      setBusy(false)
    }
  }

  return (
    <span style={{ position: 'relative' }}>
      <button
        aria-expanded={open}
        aria-label={`Who reads ${item.name}`}
        onClick={(event) => {
          const chip = event.currentTarget.getBoundingClientRect()
          setUpward(window.innerHeight - chip.bottom < POPOVER_HEIGHT)
          setOpen(!open)
        }}
        style={{ font: '500 10px var(--font-machine)', letterSpacing: '.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', padding: '3px 7px', borderRadius: radius.xs, cursor: 'pointer', color: item.shared_link ? 'var(--accent)' : 'var(--text-dim)', background: 'var(--panel-strong)', border: `1px solid ${item.shared_link ? 'var(--accent-edge)' : 'var(--border-strong)'}` }}
      >
        {label}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={`Who reads ${item.name}`}
          style={{ position: 'absolute', ...(upward ? { bottom: '100%', marginBottom: 6 } : { top: '100%', marginTop: 6 }), right: 0, zIndex: 20, width: 268, padding: 12, borderRadius: radius.sm, background: 'var(--ground-raised)', border: '1px solid var(--border-strong)', boxShadow: '0 10px 30px rgba(0,0,0,.35)' }}
        >
          {sharing.length === 0 && (
            <Reason>You are in no organisation, so there is nobody to show this to yet.</Reason>
          )}
          {sharing.map((row) => (
            <label
              key={row.organisation}
              style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '5px 0', cursor: busy ? 'default' : 'pointer' }}
            >
              <input
                type="checkbox"
                checked={named.has(row.organisation)}
                disabled={busy}
                onChange={(event) => run(() => onChange(
                  event.target.checked
                    ? [...item.audience, row.organisation]
                    : item.audience.filter((slug) => slug !== row.organisation)))}
              />
              <span style={{ fontSize: 12.5, textDecoration: row.enabled ? 'none' : 'line-through', color: row.enabled ? 'var(--text)' : 'var(--text-faint)' }}>
                {row.name}
              </span>
            </label>
          ))}
          {sharing.some((row) => !row.enabled && named.has(row.organisation)) && (
            <p style={{ margin: '4px 0 0', fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)' }}>
              Struck through means you are not sharing your setup with that
              organisation at all, so nothing reaches it however this reads.
              The switch for it is at the top of this pane.
            </p>
          )}
          <label style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '5px 0', cursor: busy ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={carried}
              disabled={busy}
              onChange={(event) => run(() => onSetupLinkChange(event.target.checked))}
            />
            <span style={{ fontSize: 12.5 }}>Anyone with my setup link</span>
          </label>
          <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>
              Anyone with this item's link
            </span>
            <Toggle
              label={`Link to ${item.name}`}
              on={Boolean(item.shared_link)}
              disabled={busy}
              onChange={(next) => run(next ? onLink : onRevoke)}
            />
          </div>
          {item.shared_link && (
            <>
              <div style={{ ...mono, marginTop: 8, padding: '6px 8px', borderRadius: radius.xs, background: 'var(--panel-strong)' }}>
                {item.shared_link}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  onClick={() => void copy(item.shared_link ?? '')}
                  style={{ padding: '5px 10px', borderRadius: radius.xs, background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', font: '600 11.5px var(--font-interface)', cursor: 'pointer' }}
                >
                  {copyLabel(copyState, 'Copy')}
                </button>
                <button
                  disabled={busy}
                  onClick={() => run(onLink)}
                  style={{ padding: '5px 10px', borderRadius: radius.xs, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', font: '600 11.5px var(--font-interface)', cursor: 'pointer' }}
                >
                  Rotate
                </button>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)' }}>
                Rotating replaces it, so whoever has the old one stops being
                able to read this.
              </p>
            </>
          )}
          {failure && (
            <p role="alert" style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--deletions)' }}>
              {failure}
            </p>
          )}
        </div>
      )}
    </span>
  )
}

//: Roughly what the popover needs with two organisations and a link showing.
//: Measured rather than computed, because it only decides which way to open and
//: being a little wrong opens it the other way rather than breaking anything.
const POPOVER_HEIGHT = 300

function Reason({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, fontFamily: 'var(--font-human)', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-dim)' }}>
      {children}
    </p>
  )
}

/**
 * What is behind an item: its files, or the reason there are none.
 *
 * An item with an origin shows that origin rather than content on purpose.
 * Keeping the origin instead of a frozen copy is what makes installing from
 * the source bring its updates along, and a copy of somebody else's file would
 * be redistribution besides.
 */
function Reading({ item }: { item: SetupItem }) {
  const [path, setPath] = useState<string | null>(null)
  const shown = item.files.find((file) => file.path === path) ?? item.files[0] ?? null

  if (item.state === 'has_origin') {
    return (
      <Reason>
        Not kept here, on purpose. There is somewhere to install this from, so
        stagepost keeps that and not a frozen copy: install it from{' '}
        <span style={{ ...mono, color: 'var(--accent)' }}>{item.origin ?? 'its source'}</span>{' '}
        and its updates come with it.
      </Reason>
    )
  }
  if (item.state === 'origin_unknown') {
    return (
      <Reason>
        Not yours, and nobody knows whose, so this can never be published. A
        guess would credit the wrong author, which is worse than an honest gap.
      </Reason>
    )
  }
  if (!shown) {
    return (
      <Reason>
        Yours, and still only on the machine that has it. The file is the
        value, so it stays there until you publish it.
      </Reason>
    )
  }
  return (
    <>
      {item.files.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
          {item.files.map((file) => (
            <button
              key={file.path}
              aria-current={file.path === shown.path}
              onClick={() => setPath(file.path)}
              style={{ font: '400 11px var(--font-machine)', padding: '4px 8px', borderRadius: radius.xs, cursor: 'pointer', overflowWrap: 'anywhere', color: file.path === shown.path ? 'var(--accent-bright)' : 'var(--text-dim)', background: file.path === shown.path ? 'var(--accent-wash)' : 'var(--panel-strong)', border: `1px solid ${file.path === shown.path ? 'var(--accent-edge)' : 'transparent'}` }}
            >
              {file.path}
            </button>
          ))}
        </div>
      )}
      <pre style={{ margin: 0, padding: 12, maxHeight: 300, overflow: 'auto', borderRadius: radius.sm, background: 'var(--ground-raised)', border: '1px solid var(--border)', font: '400 11.5px/1.6 var(--font-machine)', color: 'var(--text-dim)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {shown.content}
      </pre>
    </>
  )
}

/**
 * One row, and the only decision the pane can carry out.
 *
 * ``reference`` is the row's half of a command said once for the section. The
 * previous version of this pane printed the whole command on all fifty-five
 * rows, which buried the names a reader came for under one repeated string.
 */
function ItemRow({ item, owned, open, onOpen, onWithdraw, audience }: {
  item: SetupItem
  owned: boolean
  open: boolean
  onOpen: () => void
  onWithdraw?: () => Promise<void>
  audience?: React.ReactNode
}) {
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const reference = owned
    && (item.state === 'origin_unknown' || (item.state === 'yours' && !item.published))

  return (
    <div style={line}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          aria-expanded={open}
          onClick={onOpen}
          style={{ flex: 1, minWidth: 120, padding: 0, textAlign: 'left', background: 'transparent', cursor: 'pointer' }}
        >
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>
            {item.name}
          </span>
          {item.origin && <span style={{ ...mono, display: 'block' }}>{item.origin}</span>}
        </button>
        <StateChip item={item} owned={owned} />
        {audience}
        {reference && <Command>{keyOf(item)}</Command>}
        {onWithdraw && item.published && (
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setFailure(null)
              try {
                await onWithdraw()
              } catch (error) {
                setFailure(error instanceof Error ? error.message : 'not withdrawn')
              } finally {
                setBusy(false)
              }
            }}
            style={{ padding: '7px 13px', borderRadius: radius.sm, background: 'rgba(229,98,74,.12)', color: 'var(--deletions)', fontSize: 12, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}
          >
            {busy ? 'Withdrawing…' : 'Withdraw'}
          </button>
        )}
      </div>
      {open && <div style={{ margin: '10px 0 4px' }}><Reading item={item} /></div>}
      {failure && (
        <div role="alert" style={{ fontSize: 11.5, color: 'var(--deletions)' }}>{failure}</div>
      )}
    </div>
  )
}

/** One kind's list: the rule it lives by, what it withholds, and its rows. */
function KindList({ section, onWithdraw, sharing, onAudience, onLink, onRevoke, carryOne }: {
  section: Section
  onWithdraw: (item: SetupItem) => Promise<void>
  sharing: OrganisationSharing[]
  onAudience: (item: SetupItem, organisations: string[]) => Promise<void>
  onLink: (item: SetupItem) => Promise<void>
  onRevoke: (item: SetupItem) => Promise<void>
  carryOne: (reference: string, next: boolean) => Promise<void>
}) {
  const [open, setOpen] = useState<string | null>(null)
  const unpublished = section.items.some((item) => item.state === 'yours' && !item.published)
  const unattributed = section.items.some((item) => item.state === 'origin_unknown')

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{section.label}</span>
        <span style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 12, color: 'var(--text-dim)' }}>
          {section.rule}
        </span>
      </div>
      {/* Each command is said once for the section and each row carries its
          own argument, because a real record repeats these rows dozens of
          times and the names are what a reader came for. */}
      {unpublished && (
        <p style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text-dim)', margin: '10px 0 0' }}>
          Private until you publish it, one at a time, from the machine holding
          the file: <Command>stagepost setup --publish &lt;name&gt;</Command>
        </p>
      )}
      {unattributed && (
        <p style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text-dim)', margin: '10px 0 0' }}>
          Nothing whose origin is missing can be published. Say where one came
          from with <Command>stagepost setup --origin &lt;kind&gt;/&lt;name&gt; &lt;origin&gt;</Command>,
          or <Command>--mine</Command> if you wrote it after all.
        </p>
      )}
      <div style={{ marginTop: 12 }}>
        {section.items.map((item) => (
          <ItemRow
            key={keyOf(item)}
            item={item}
            owned
            open={open === keyOf(item)}
            onOpen={() => setOpen(open === keyOf(item) ? null : keyOf(item))}
            onWithdraw={() => onWithdraw(item)}
            audience={item.published ? (
              <Audience
                item={item}
                sharing={sharing}
                onChange={(organisations) => onAudience(item, organisations)}
                onLink={() => onLink(item)}
                onRevoke={() => onRevoke(item)}
                carried={item.on_setup_link}
                onSetupLinkChange={(next) => carryOne(keyOf(item), next)}
              />
            ) : undefined}
          />
        ))}
      </div>
    </>
  )
}

/** The kind rail: the top-level split, and the only navigation the pane has. */
function KindRail({ sections, active, onPick }: {
  sections: Section[]
  active: string
  onPick: (kind: string) => void
}) {
  return (
    <div className="sp-scroller" style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
      {sections.map((section) => (
        <button
          key={section.kind}
          aria-current={section.kind === active}
          onClick={() => onPick(section.kind)}
          style={{ flex: 'none', display: 'flex', gap: 7, alignItems: 'baseline', padding: '8px 12px', borderRadius: radius.sm, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: section.kind === active ? 700 : 500, color: section.kind === active ? 'var(--text)' : 'var(--text-dim)', background: section.kind === active ? 'var(--panel-strong)' : 'transparent' }}
        >
          {section.label}
          <span style={{ font: '500 11px var(--font-machine)', color: section.kind === active ? 'var(--accent)' : 'var(--text-faint)' }}>
            {section.items.length}
          </span>
        </button>
      ))}
    </div>
  )
}

/** Somebody else's record, led by the part of it you do not already have. */
function MemberRecord({ theirs, mine, onBack }: {
  theirs: Setup
  mine: Setup | null
  onBack: () => void
}) {
  const ours = useMemo(() => itemsIn(mine), [mine])
  // Marketplaces fold away here too. Nobody installs one; they install the
  // plugin it carries, and that plugin names it.
  const all = [...itemsIn(theirs).values()].filter((item) => item.kind !== 'marketplace')
  const missing = all.filter((item) => !ours.has(keyOf(item)))
  const both = all.filter((item) => ours.has(keyOf(item)))
  const [open, setOpen] = useState<string | null>(null)

  return (
    <>
      <button
        onClick={onBack}
        style={{ padding: '7px 12px', marginBottom: 16, borderRadius: radius.sm, background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
      >
        ← Everyone
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 22 }}>
        <Face name={theirs.display_name} avatarKey={theirs.avatar_key} size={40} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{theirs.display_name}</div>
          <div style={mono}>
            {theirs.machines.map((machine) =>
              `${machine.name} (${machine.platform}, reported ${reportedOn(machine)})`)
              .join(' · ') || 'no machines reported'}
          </div>
        </div>
      </div>

      <section aria-label="Not on your machines" style={{ marginBottom: 26 }}>
        <Label>Not on your machines</Label>
        {missing.length === 0 ? (
          <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)' }}>
            Nothing. You already run everything {theirs.display_name} shares.
          </p>
        ) : missing.map((item) => (
          <ItemRow
            key={keyOf(item)}
            item={item}
            owned={false}
            open={open === keyOf(item)}
            onOpen={() => setOpen(open === keyOf(item) ? null : keyOf(item))}
          />
        ))}
      </section>

      <section aria-label="You both run">
        <Label>You both run</Label>
        {both.length === 0 ? (
          <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)' }}>
            Nothing in common yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {both.map((item) => (
              <span key={keyOf(item)} style={{ ...mono, padding: '5px 9px', borderRadius: radius.xs, background: 'var(--panel-strong)' }}>
                {item.name}
              </span>
            ))}
          </div>
        )}
      </section>

      <Aside>
        What is missing here is what a plan would install. A record is never
        applied for you, and stagepost never writes your files.
      </Aside>
    </>
  )
}

/** How a row is addressed: its identity, or its label when it has none. */
const addressOf = (machine: Machine) => machine.machine_identifier ?? machine.name

/** What a refusal calls a row recorded before there were identities. */
const NO_IDENTITY = '(no identity)'

/**
 * The identities a refusal names.
 *
 * A label stopped being unique the moment machines carried identities, so a
 * label naming two rows is refused rather than guessed at, and the refusal
 * says which identities it could have meant. Reading them back out is what
 * turns that refusal into a choice instead of a dead end.
 */
function identitiesNamed(detail: string): string[] {
  const said = detail.lastIndexOf('identity:')
  if (said < 0) return []
  return detail.slice(said + 'identity:'.length)
    .split(',')
    .map((identity) => identity.trim())
    .filter((identity) => identity !== '')
}

/** An identity, with whatever the record already knows about the row wearing it. */
function describeIdentity(identity: string, machines: Machine[]): string {
  const row = machines.find((machine) => machine.machine_identifier === identity)
  return row ? `${identity} · reported ${reportedOn(row)}` : identity
}

// Destructive, so the wash and the red rather than a solid button, which is
// what withdrawing a publication already looks like two sections down.
const forgetting = {
  padding: '5px 11px', borderRadius: radius.xs, background: 'rgba(229,98,74,.12)',
  color: 'var(--deletions)', font: '700 11.5px var(--font-interface)',
} as const
const inChip = {
  margin: '7px 0 0', font: '400 12px/1.5 var(--font-interface)',
} as const

/**
 * One machine, and the one erasure this pane can carry out.
 *
 * The identity goes on the wire wherever there is one, because the pane knows
 * which row was clicked and nobody should be made to disambiguate what is
 * already settled. A row recorded before identities existed can be pointed
 * at only by its label, and a label two rows share is refused, which is why
 * that refusal is a step in this flow rather than an error to print.
 */
function MachineChip({ machine, machines, onForget, onDefault, onPermissionMode }: {
  machine: Machine
  machines: Machine[]
  onForget: (address: string) => Promise<void>
  onDefault: (machineId: string | null) => Promise<void>
  onPermissionMode: (machineId: string, permissionMode: string | null) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [choosing, setChoosing] = useState(false)
  const [settingMode, setSettingMode] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [named, setNamed] = useState<string[]>([])
  const pickable = named.filter((identity) => identity !== NO_IDENTITY)

  async function forget(address: string) {
    setBusy(true)
    setRefusal(null)
    setNamed([])
    try {
      await onForget(address)
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : 'not forgotten')
      if (error instanceof ApiError && error.status === 409) {
        setNamed(identitiesNamed(error.message))
      }
    } finally {
      setBusy(false)
    }
  }

  // One default at a time: the server moves it off whichever machine had it.
  async function choose(on: boolean) {
    setChoosing(true)
    setRefusal(null)
    try {
      await onDefault(on ? machine.id : null)
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : 'not changed')
    } finally {
      setChoosing(false)
    }
  }

  // The empty value is no choice, which leaves the mode to the runner.
  async function chooseMode(permissionMode: string) {
    setSettingMode(true)
    setRefusal(null)
    try {
      await onPermissionMode(machine.id, permissionMode || null)
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : 'not changed')
    } finally {
      setSettingMode(false)
    }
  }

  return (
    <div style={{ ...mono, maxWidth: '100%', padding: '6px 10px', borderRadius: radius.xs, background: 'var(--panel-strong)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span>
          {machine.name} · {machine.platform} · {machine.items.length}
          {machine.items.length === 1 ? ' item' : ' items'}
          {' '}· reported {reportedOn(machine)}
          {machine.runner_online
            ? ' · runner online'
            : machine.runner_seen_at ? ` · runner last seen ${describeAge(machine.runner_seen_at)}` : null}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          Default for new agents
          <Toggle
            on={machine.default}
            onChange={(on) => void choose(on)}
            label={`Default for new agents: ${machine.name}`}
            disabled={choosing}
          />
        </span>
        {machine.permission_modes.length > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            Agents start in
            <select
              aria-label={`Agents start in: ${machine.name}`}
              value={machine.permission_mode ?? ''}
              // Not `disabled`: Chrome drops the focus of a control it disables,
              // and a keyboard user would lose their place on every save.
              aria-disabled={settingMode}
              onChange={(event) => { if (!settingMode) void chooseMode(event.target.value) }}
              // Dark, or Windows and Linux open the list as light text on white.
              style={{ font: 'inherit', color: 'var(--text)', background: 'transparent', border: '1px solid var(--border)', borderRadius: radius.xs, padding: '2px 4px', cursor: settingMode ? 'default' : 'pointer', colorScheme: 'dark', minWidth: 0, maxWidth: '100%' }}
            >
              <option value="">
                {machine.permission_mode_default
                  ? `machine default (${machine.permission_mode_default})`
                  : 'machine default'}
              </option>
              {machine.permission_modes.map((mode) => (
                <option key={mode} value={mode}>
                  {mode === machine.permission_mode_default ? `${mode} (default)` : mode}
                </option>
              ))}
            </select>
          </span>
        )}
        <button
          disabled={busy}
          aria-label={`Forget ${machine.name}`}
          onClick={() => void forget(addressOf(machine))}
          style={{ ...forgetting, cursor: busy ? 'default' : 'pointer' }}
        >
          {busy ? 'Forgetting…' : 'Forget'}
        </button>
      </div>
      {machine.permission_modes_withheld.map((mode) => (
        <p key={mode} style={{ ...inChip, color: 'var(--text-dim)' }}>
          {mode === 'bypassPermissions' ? 'Bypass permissions' : mode} needs{' '}
          <Command>stagepost runner install --allow-bypass</Command> on this machine.
        </p>
      ))}
      {refusal && (
        <p role="alert" style={{ ...inChip, color: 'var(--deletions)' }}>{refusal}</p>
      )}
      {pickable.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 7 }}>
          {pickable.map((identity) => (
            <button
              key={identity}
              disabled={busy}
              onClick={() => void forget(identity)}
              style={{ ...forgetting, cursor: busy ? 'default' : 'pointer' }}
            >
              Forget {describeIdentity(identity, machines)}
            </button>
          ))}
        </div>
      )}
      {named.length > pickable.length && (
        <p style={{ ...inChip, color: 'var(--text-dim)' }}>
          One of them was recorded before there were identities, so nothing can
          point at it on its own yet. It is given one the next time it reports.
        </p>
      )}
    </div>
  )
}

/** The link, and the two things a person does with it. */
function SetupLinkCard({ link, busy, onMint, onRevoke }: {
  link: string | null
  busy: boolean
  onMint: () => Promise<void>
  onRevoke: () => Promise<void>
}) {
  const [linkState, copyLink] = useCopyState()
  const [agentState, copyAgent] = useCopyState()

  if (!link) {
    return (
      <div style={{ padding: '14px 0 18px' }}>
        <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', margin: '0 0 10px' }}>
          One link to everything you choose to share. Whoever holds it can
          install your setup without an account.
        </p>
        <button
          disabled={busy}
          onClick={() => void onMint()}
          style={{ padding: '9px 15px', borderRadius: radius.sm, background: 'var(--accent-wash)', border: '1px solid var(--accent-edge)', color: 'var(--accent-bright)', font: '700 12.5px var(--font-interface)', cursor: busy ? 'default' : 'pointer' }}
        >
          {busy ? 'Creating…' : 'Create a link'}
        </button>
      </div>
    )
  }
  return (
    <div style={{ padding: '4px 0 18px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ ...mono, flex: 1, minWidth: 180, padding: '8px 10px', borderRadius: radius.xs, background: 'var(--panel-strong)' }}>
          {link}
        </div>
        <button
          onClick={() => void copyLink(link)}
          style={{ padding: '9px 15px', borderRadius: radius.sm, background: 'var(--accent-wash)', border: '1px solid var(--accent-edge)', color: 'var(--accent-bright)', font: '700 12.5px var(--font-interface)', cursor: 'pointer' }}
        >
          {copyLabel(linkState, 'Copy link')}
        </button>
        <button
          onClick={() => void copyAgent(agentMessage(link))}
          style={{ padding: '9px 15px', borderRadius: radius.sm, background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', font: '700 12.5px var(--font-interface)', cursor: 'pointer' }}
        >
          {copyLabel(agentState, 'Copy for an agent')}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
        <button
          disabled={busy}
          onClick={() => void onMint()}
          style={{ background: 'transparent', color: 'var(--text-faint)', font: '600 11.5px var(--font-interface)', cursor: busy ? 'default' : 'pointer' }}
        >
          Rotate
        </button>
        <button
          disabled={busy}
          onClick={() => void onRevoke()}
          style={{ background: 'transparent', color: 'var(--deletions)', font: '600 11.5px var(--font-interface)', cursor: busy ? 'default' : 'pointer' }}
        >
          Revoke
        </button>
      </div>
      <p style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)', margin: '8px 0 0' }}>
        Rotating replaces it, so whoever has the old one stops being able to
        read this. What it carries stays as you left it.
      </p>
    </div>
  )
}

/**
 * Who reads the record link, and the two controls that change it.
 *
 * "Add all" sends `published` directly through `onChange`: that array
 * doesn't depend on what is currently carried, so it isn't exposed to the
 * race the individual checkboxes are. Each checkbox instead calls `carryOne`
 * by reference, the same function a row's own popover calls, so a tick here
 * and a tick on a row queue behind one another and each reads the record the
 * one before it actually wrote, rather than a `carried` array this component
 * computed once at render time and would otherwise still be holding stale by
 * the time its own turn in the queue came up.
 */
function LinkAudience({ published, carried, onChange, carryOne }: {
  published: string[]
  carried: string[]
  onChange: (references: string[]) => Promise<void>
  carryOne: (reference: string, next: boolean) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const carrying = new Set(carried)

  async function run(work: () => Promise<void>) {
    setBusy(true)
    setFailure(null)
    try {
      await work()
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'not saved')
    } finally {
      setBusy(false)
    }
  }

  return (
    <span style={{ position: 'relative' }}>
      <button
        aria-expanded={open}
        aria-label="Who reads this link"
        onClick={() => setOpen(!open)}
        style={{ font: '500 11px var(--font-machine)', whiteSpace: 'nowrap', padding: '5px 9px', borderRadius: radius.xs, cursor: 'pointer', color: 'var(--text-dim)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)' }}
      >
        {carried.length} of {published.length} published
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="What this link carries"
          style={{ position: 'absolute', top: '100%', marginTop: 6, right: 0, zIndex: 20, width: 288, maxHeight: 340, overflowY: 'auto', padding: 12, borderRadius: radius.sm, background: 'var(--ground-raised)', border: '1px solid var(--border-strong)', boxShadow: '0 10px 30px rgba(0,0,0,.35)' }}
        >
          {published.length === 0 ? (
            <Reason>
              You have published nothing yet, so the link has nothing to carry.
              Publish an item from the machine holding its file and it turns up
              here.
            </Reason>
          ) : (
            <>
              <button
                disabled={busy || carried.length === published.length}
                onClick={() => void run(() => onChange(published))}
                style={{ width: '100%', marginBottom: 8, padding: '7px 10px', borderRadius: radius.xs, background: 'var(--accent-wash)', border: '1px solid var(--accent-edge)', color: 'var(--accent-bright)', font: '700 11.5px var(--font-interface)', cursor: busy ? 'default' : 'pointer' }}
              >
                Add all {published.length}
              </button>
              {published.map((reference) => (
                <label
                  key={reference}
                  style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0', cursor: busy ? 'default' : 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={carrying.has(reference)}
                    disabled={busy}
                    onChange={(event) => void run(() => carryOne(reference, event.target.checked))}
                  />
                  <span style={{ ...mono, color: 'var(--text)' }}>{reference}</span>
                </label>
              ))}
              <p style={{ margin: '8px 0 0', fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)' }}>
                Nothing joins the link on its own. An item you publish later
                stays off it until you say otherwise.
              </p>
            </>
          )}
          {failure && (
            <p role="alert" style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--deletions)' }}>
              {failure}
            </p>
          )}
        </div>
      )}
    </span>
  )
}

/** `machines` is the organisation stream's, newer than the fetched record on whether each runner is up. */
export function SetupPane({ me, organisation, machines }: { me: Me; organisation: Organisation; machines: OrganisationMachine[] | null }) {
  const withLiveRunner = (machine: Machine): Machine => {
    const heard = machines?.find((candidate) => candidate.id === machine.id)
    return heard ? { ...machine, runner_online: heard.runner_online, runner_seen_at: heard.runner_off_since } : machine
  }
  const [mine, setMine] = useState<Setup | null>(null)
  // Mirrors `mine`, written the instant a write's own refresh comes back
  // rather than whenever React gets around to committing the next render.
  // `carryOne` below reads this, not `mine`, because a queued write has to
  // see what the write ahead of it actually did, and React's own timing
  // gives no guarantee of that by the time the next one runs.
  const mineRef = useRef<Setup | null>(null)
  // Every write that changes what the record link carries goes through this
  // queue, one at a time. `setSetupLinkItems` replaces the whole set, so two
  // ticks racing off the same locally-held record would each compute a
  // target that doesn't know about the other's change, and whichever write
  // reaches the server last would silently drop it. Queuing the second
  // behind the first, rather than merging their intents on the client, means
  // the second always computes its target after the first has actually
  // landed and been read back, which is the only version of "what is carried
  // right now" the server will agree with.
  const carryQueue = useRef<Promise<void>>(Promise.resolve())
  const [sharers, setSharers] = useState<SetupSummary[]>([])
  const [reading, setReading] = useState<Setup | null>(null)
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [readFailure, setReadFailure] = useState<string | null>(null)
  const [kind, setKind] = useState<string | null>(null)

  function applyMine(setup: Setup | null) {
    mineRef.current = setup
    setMine(setup)
  }

  useEffect(() => {
    void api.mySetup().then(applyMine)
    void api.setups(organisation.slug).then(setSharers)
  }, [organisation.slug])

  // Deduped across machines first: two laptops running one plugin run it once
  // as far as anybody reading this is concerned.
  const sections = useMemo(() => sectionsOf(itemsIn(mine).values()), [mine])
  // The rail's pick, else skills, which is the pile where whose-is-it bites
  // and the one the prototype opened on. Held loosely so a kind that empties
  // out while the pane is open falls back rather than heading nothing.
  const shown = sections.find((section) => section.kind === kind)
    ?? sections.find((section) => section.kind === 'skill') ?? sections[0] ?? null

  if (reading) {
    return <MemberRecord theirs={reading} mine={mine} onBack={() => setReading(null)} />
  }

  const sharing = mine?.sharing ?? []
  const others = sharers.filter((sharer) => sharer.id !== me.id)

  async function withdraw(item: SetupItem) {
    await api.unpublishItem(item.kind, item.name)
    applyMine(await api.mySetup())
  }

  // Each of these re-reads the record rather than patching the item in place.
  // The audience lives on the publication and the same publication appears on
  // every machine that reports the item, so patching one row would leave the
  // others saying something that stopped being true.
  async function aim(item: SetupItem, organisations: string[]) {
    await api.setPublicationAudience(item.kind, item.name, organisations)
    applyMine(await api.mySetup())
  }

  function serially(work: () => Promise<void>): Promise<void> {
    const scheduled = carryQueue.current.then(work, work)
    carryQueue.current = scheduled.catch(() => undefined)
    return scheduled
  }

  /** The write itself, re-reading the record rather than patching in place
   * for the reason `aim` does. Always called from inside `serially`, never
   * on its own, so it never runs while another link-carrying write is still
   * in flight. */
  async function writeLinkItems(references: string[]) {
    await api.setSetupLinkItems(references)
    applyMine(await api.mySetup())
  }

  function aimLink(references: string[]): Promise<void> {
    return serially(() => writeLinkItems(references))
  }

  /**
   * One reference's carried state, whether the tick came from a row's own
   * popover or from the link's own "who reads this link" popover: both call
   * this by reference now, rather than either computing its own array, so
   * two ticks fired from two different controls queue behind one another
   * the same way two ticks on two different rows already did.
   *
   * Reads the carried set only once its turn in the queue arrives, off
   * `mineRef` rather than a `carried` array either caller closed over
   * earlier: by then, anything queued ahead of it has already written and
   * refreshed, so this adds or removes its own reference on top of that
   * record, never on top of a snapshot that doesn't know about it yet.
   */
  function carryOne(reference: string, next: boolean): Promise<void> {
    return serially(() => {
      const carried = carriedReferences(mineRef.current)
      return writeLinkItems(next
        ? [...carried, reference].sort()
        : carried.filter((entry) => entry !== reference))
    })
  }

  async function link(item: SetupItem) {
    await api.mintShareLink(item.kind, item.name)
    applyMine(await api.mySetup())
  }

  async function unlink(item: SetupItem) {
    await api.revokeShareLink(item.kind, item.name)
    applyMine(await api.mySetup())
  }

  // Refusals are the chip's to show, so this deliberately does not catch: a
  // label naming two rows comes back as a choice, not as a failed click.
  async function forget(address: string) {
    await api.forgetMachine(address)
    applyMine(await api.mySetup())
  }

  async function chooseDefault(machineId: string | null) {
    await api.setDefaultMachine(machineId)
    applyMine(await api.mySetup())
  }

  async function choosePermissionMode(machineId: string, permissionMode: string | null) {
    await api.setPermissionMode(machineId, permissionMode)
    applyMine(await api.mySetup())
  }

  return (
    <>
      <Label>Your setup</Label>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4 }}>
        <a
          href={api.setupExportUrl()}
          download
          style={{ padding: '9px 15px', borderRadius: radius.sm, background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', font: '700 12.5px var(--font-interface)', textDecoration: 'none' }}
        >
          Export
        </a>
        <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
          A zip with a PDF guide and your published files.
        </span>
      </div>
      <SetupLinkCard
        link={mine?.setup_link ?? null}
        busy={saving}
        onMint={async () => {
          setSaving(true)
          setFailure(null)
          try {
            await api.mintSetupLink()
            applyMine(await api.mySetup())
          } catch (error) {
            setFailure(error instanceof Error ? error.message : 'not created')
          } finally {
            setSaving(false)
          }
        }}
        onRevoke={async () => {
          setSaving(true)
          setFailure(null)
          try {
            await api.revokeSetupLink()
            applyMine(await api.mySetup())
          } catch (error) {
            setFailure(error instanceof Error ? error.message : 'not revoked')
          } finally {
            setSaving(false)
          }
        }}
      />

      <Label>Who has access</Label>
      <div role="group" aria-label="Who has access">
        {mine?.setup_link && (
          <div style={{ ...line, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>Anyone with the link</div>
              <div style={mono}>no account needed</div>
            </div>
            <LinkAudience
              published={publishedReferences(mine)}
              carried={carriedReferences(mine)}
              onChange={aimLink}
              carryOne={carryOne}
            />
          </div>
        )}
        {sharing.map((row) => (
          <div
            key={row.organisation}
            style={{ ...line, display: 'flex', alignItems: 'center', gap: 16 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>{row.name}</div>
              <div style={mono}>{row.organisation}</div>
            </div>
            <Toggle
              label={`Share my setup with ${row.name}`}
              on={row.enabled}
              disabled={saving || !mine}
              onChange={async (next) => {
                setSaving(true)
                setFailure(null)
                try {
                  const saved = await api.setSetupSharing(row.organisation, next)
                  const current = mineRef.current
                  applyMine(current && {
                    ...current,
                    sharing: current.sharing.map((entry) =>
                      entry.organisation === saved.organisation ? saved : entry),
                  })
                  setSharers(await api.setups(organisation.slug))
                } catch (error) {
                  setFailure(error instanceof Error ? error.message : 'not saved')
                } finally {
                  setSaving(false)
                }
              }}
            />
          </div>
        ))}
      </div>
      {failure && (
        <p role="alert" style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--deletions)' }}>
          {failure}
        </p>
      )}
      <div style={{ height: 26 }} />
      {shown && (
        <div style={{ marginBottom: 26 }}>
          <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', margin: '-4px 0 14px' }}>
            Split by kind, and every row says whose it is. Something with an
            origin is fully described by that origin, so it is reported for
            you. Something you wrote is a file, and the file is the value, so
            it stays private until you publish it.
          </p>
          <KindRail sections={sections} active={shown.kind} onPick={setKind} />
          <KindList
            key={shown.kind}
            section={shown}
            onWithdraw={withdraw}
            sharing={sharing}
            onAudience={aim}
            onLink={link}
            onRevoke={unlink}
            carryOne={carryOne}
          />
        </div>
      )}

      <Label>Your machines</Label>
      {mine?.machines.length ? (
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {mine.machines.map((machine) => (
              <MachineChip
                key={machine.machine_identifier ?? machine.name}
                machine={withLiveRunner(machine)}
                machines={mine.machines}
                onForget={forget}
                onDefault={chooseDefault}
                onPermissionMode={choosePermissionMode}
              />
            ))}
          </div>
          <p style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text-dim)', margin: '10px 0 0' }}>
            Forgetting one drops what it reported and nothing else. Nothing on
            the machine itself changes, so one that is still running comes back
            on its next report.
          </p>
        </div>
      ) : (
        <div style={{ padding: '4px 0 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)', margin: 0 }}>
            No machine has reported yet. Run <Command>stagepost setup</Command> on
            one and it turns up here. A machine without the command gets it with:
          </p>
          <InstallCommand />
        </div>
      )}

      <Label>Everyone in {organisation.name}</Label>
      {others.length === 0 ? (
        <p style={{ fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-faint)', padding: '4px 0' }}>
          Nobody here shares a setup yet.
        </p>
      ) : others.map((sharer) => (
        <button
          key={sharer.id}
          onClick={async () => {
            setReadFailure(null)
            try {
              setReading(await api.memberSetup(organisation.slug, sharer.id))
            } catch (error) {
              setReadFailure(error instanceof Error ? error.message : 'not read')
            }
          }}
          style={{ ...line, width: '100%', display: 'flex', alignItems: 'center', gap: 13, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
        >
          <Face name={sharer.display_name} avatarKey={sharer.avatar_key} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
              {sharer.display_name}
            </span>
            <span style={{ ...mono, display: 'block' }}>
              {sharer.machines.join(', ') || 'no machines'} · {sharer.published_count} published
            </span>
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Compare →</span>
        </button>
      ))}

      {readFailure && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--deletions)' }}>
          {readFailure}
        </p>
      )}

      <Aside>
        A machine that stopped reporting stays in the list, ageing in front of
        you. That says more than hiding it would.
      </Aside>
    </>
  )
}
